import { lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import MainLayout from "@/layouts/Layout";

const CreateCardRedirect = () => {
    const { search } = useLocation();
    return <Navigate to={`/createLawyerCard${search}`} replace />;
};

const Login = lazy(() => import("@/pages/login"));
const Loading = () => <div className="loading">加载中...</div>;
const Home = lazy(() => import("@/pages/legalMindUser/home"));

const Notifications = lazy(() => import("@/pages/notifications"));
const ComplaintFeedback = lazy(() => import("@/pages/complaintFeedback"));

const OrgManagement = lazy(() => import("@/pages/system/orgManagement"));
const AccountManagement = lazy(() => import("@/pages/system/accountManagement"));
const RoleManagement = lazy(() => import("@/pages/system/roleManagement"));
const SuperAdminAccount = lazy(() => import("@/pages/system/superAdminAccount"));

const ComplaintManagement = lazy(() => import("@/pages/renji/complaintManagement"));
const AdditionalSlot = lazy(() => import("@/pages/renji/additionalSlot"));

const ChatRecord = lazy(() => import("@/pages/chatRecord/index"));
const JiafatongChatRecord = lazy(() => import("@/pages/jiafatong/chatRecord/index"));
const JiafatongAgentConfig = lazy(() => import("@/pages/jiafatong/agentConfig/index"));

const ELawyer = lazy(() => import("@/pages/qudao/eLawyer"));
const TrialRecord = lazy(() => import("@/pages/qudao/trialRecord"));
const LegalMindUser = lazy(() => import("@/pages/legalMindUser"));
const WechatApplyList = lazy(() => import("@/pages/qudao/wechatApplyList"));
const LawFirmConfig = lazy(() => import("@/pages/organizations/lawyer/lawFirmConfig"));
const CreateLawyerCard = lazy(() => import("@/pages/organizations/lawyer/createLawyerCard"));
const LawFirmConfigCreate = lazy(() => import("@/pages/organizations/lawyer/createLawFirmConfig"));
const AiTweetList = lazy(() => import("@/pages/content/aiTweet"));
const AiTweetCreate = lazy(() => import("@/pages/content/aiTweet/create"));
const AiTweetData = lazy(() => import("@/pages/content/aiTweet/data"));

const CardAudit = lazy(() => import("@/pages/cardAudit"));
const YydhUserManage = lazy(() => import("@/pages/yydhUserManage"));
const ChatFeedback = lazy(() => import("@/pages/chatFeedback"));
const AgentManage = lazy(() => import("@/pages/agentManage"));
const AgentManageEdit = lazy(() => import("@/pages/agentManage/edit"));

const lazyLoad = (Component: React.ComponentType) => {
    return (
        <Suspense fallback={<Loading />}>
            <Component />
        </Suspense>
    );
};

const routes: any[] = [
    {
        path: "/login",
        element: lazyLoad(Login),
    },
    {
        path: "/createLawyerCard",
        element: lazyLoad(CreateLawyerCard),
    },
    {
        path: "/law-firm-config/create",
        element: lazyLoad(LawFirmConfigCreate),
    },
    {
        path: "/createCard",
        element: <CreateCardRedirect />,
    },
    {
        path: "/",
        element: <MainLayout />,
        children: [
            {
                index: true,
                element: <Navigate to="/home" />,
            },
            {
                path: "/notifications",
                authId: 'msg_00001',
                element: lazyLoad(Notifications),
            },
            {
                path: "/chat-record",
                authId: 'card_00005',
                element: lazyLoad(ChatRecord),
            },
            {
                path: "/card-audit",
                authId: 'card_00006',
                element: lazyLoad(CardAudit),
            },
            {
                path: "/yydh-user-manage",
                authId: 'card_00008',
                element: lazyLoad(YydhUserManage),
            },
            {
                path: "/chat-feedback",
                authId: 'card_00007',
                element: lazyLoad(ChatFeedback),
            },
            {
                path: "/agent-manage",
                authId: 'card_00009',
                element: lazyLoad(AgentManage),
            },
            {
                path: "/agent-manage/edit",
                authId: 'card_00009',
                element: lazyLoad(AgentManageEdit),
            },
            {
                path: "/law-chat-record",
                authId: 'jiafatong_00002',
                element: lazyLoad(JiafatongChatRecord),
            },
            {
                path: "/ai-agent-setting",
                authId: 'jiafatong_00003',
                element: lazyLoad(JiafatongAgentConfig),
            },
            {
                path: "/jiafatong/chat-record",
                element: <Navigate to="/law-chat-record" replace />,
            },
            {
                path: "/complaint-feedback",
                authId: 'card_00004',
                element: lazyLoad(ComplaintFeedback),
            },
            {
                path: "/org-management",
                authId: 'sys_00002',
                element: lazyLoad(OrgManagement),
            },
            {
                path: "/account-management",
                authId: 'sys_00003',
                element: lazyLoad(AccountManagement),
            },
            {
                path: "/role-management",
                authId: 'sys_00004',
                element: lazyLoad(RoleManagement),
            },
            {
                path: "/super-admin-account",
                authId: 'sys_00005',
                element: lazyLoad(SuperAdminAccount),
            },
            {
                path: "/complaint-management",
                authId: 'rj_00002',
                element: lazyLoad(ComplaintManagement),
            },
            {
                path: "/additional-slot",
                authId: 'rj_00003',
                element: lazyLoad(AdditionalSlot),
            },
            {
                path: "/home",
                element: lazyLoad(Home),
            },
            {
                path: "/trial-record",
                authId: 'elawyer_00002',
                element: lazyLoad(TrialRecord),
            },
            {
                path: "/e-lawyer",
                authId: 'elawyer_00003',
                element: lazyLoad(ELawyer),
            },
            {
                path: "/wechat-apply-list",
                authId: 'card_00003',
                element: lazyLoad(WechatApplyList),
            },
            {
                path: "/legalmind-user",
                authId: 'card_00002',
                element: lazyLoad(LegalMindUser),
            },
            {
                path: "/law-firm-config",
                authId: 'firm_00002',
                element: lazyLoad(LawFirmConfig),
            },
            {
                path: "/ai-tweet",
                authId: 'firm_00003',
                element: lazyLoad(AiTweetList),
            },
            {
                path: "/ai-tweet/create",
                authId: 'firm_00003',
                element: lazyLoad(AiTweetCreate),
            },
            {
                path: "/ai-tweet/:id/data",
                authId: 'firm_00003',
                element: lazyLoad(AiTweetData),
            },
        ],
    },
    {
        path: "*",
        element: <Navigate to="/home" />,
    },
];

const protectedRoute = (routes: any[]) => {
    const result: any[] = [];
    function walk(route: any) {
        if ('authId' in route) {
            result.push({
                path: route.path,
                authId: route.authId
            });
        }
        if (route.children && Array.isArray(route.children)) {
            route.children.forEach((child: any) => walk(child));
        }
    }
    routes.forEach(route => walk(route));
    return result;
};

export const protectedRoutes = protectedRoute(routes);
export default routes;
